import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertTriangle, X, ArrowRight, Package, BadgeCheck, RotateCcw } from 'lucide-react';

/**
 * Pre-handover review summary (soft gate).
 * Shows open items a human should check before generating / emailing the package.
 * - mode="pre-generate": shows a proceed button that calls onProceed()
 * - mode="post-build":  informational nudge after Build-from-Drawings completes
 */
export default function HandoverReviewModal({
  projectId,
  mode = 'pre-generate',
  proceedLabel = 'Generate anyway',
  title,
  subtitle,
  onProceed,
  onClose,
}) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/handover/review-summary`);
      setSummary(res.data);
    } catch (e) {
      setSummary({ error: true });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const toggleAck = async (key) => {
    setAcking(key);
    try {
      const res = await api.post(`/closeout/projects/${projectId}/handover/ack`, { key });
      toast.success(res.data.acknowledged ? 'Section marked reviewed' : 'Sign-off removed');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update sign-off');
    } finally {
      setAcking(null);
    }
  };

  const openItems = (summary?.items || []).filter(i => i.count > 0);
  const clean = summary && !summary.error && openItems.length === 0;
  const canReview = summary?.can_review;
  const allAcked = summary?.all_open_acknowledged;

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
    catch { return ''; }
  };

  const goReview = (tab) => {
    navigate(`/closeout/${projectId}?tab=${tab}`);
    onClose();
  };

  const defaultTitle = mode === 'post-build' ? 'Register built — review before handover' : 'Review before handover';
  const defaultSubtitle = mode === 'post-build'
    ? 'The drawing pipeline finished. Check these open items, then generate the handover when you are satisfied.'
    : 'Make sure these items are correct before the package goes to the owner. This is a check, not a block.';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      data-testid="handover-review-modal" onClick={onClose}>
      <div className="bg-card border border-border rounded-md shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="flex items-start gap-2.5">
            <Package className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-heading uppercase tracking-wider text-foreground" data-testid="handover-review-title">
                {title || defaultTitle}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">{subtitle || defaultSubtitle}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="handover-review-close"
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking the package…
            </div>
          ) : summary?.error ? (
            <div className="text-xs text-red-500 py-4">Could not load the review summary. You can still proceed.</div>
          ) : clean ? (
            <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400 py-4"
              data-testid="handover-review-clean">
              <CheckCircle2 className="w-5 h-5" />
              Everything looks reviewed — {summary.total_assets} assets and {summary.total_warranties} warranties, no open items.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="w-4 h-4" />
                  {summary.open_count} open item{summary.open_count !== 1 ? 's' : ''} across {openItems.length} categor{openItems.length !== 1 ? 'ies' : 'y'}
                </div>
                <span className="text-[11px] text-muted-foreground font-mono" data-testid="handover-review-ack-count">
                  {summary.acknowledged_categories}/{openItems.length} reviewed
                </span>
              </div>
              <div className="space-y-2">
                {openItems.map(item => (
                  <div key={item.key}
                    data-testid={`review-item-${item.key}`}
                    className={`p-3 rounded-sm border transition-colors ${
                      item.acknowledged
                        ? 'border-emerald-300 dark:border-emerald-800/50 bg-emerald-50/60 dark:bg-emerald-950/20'
                        : 'border-border bg-muted/30'
                    }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-600 dark:text-amber-400">
                            {item.count}
                          </span>
                          <span className="text-xs font-medium text-foreground">{item.label}</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">{item.hint}</p>
                      </div>
                      <button onClick={() => goReview(item.tab)}
                        data-testid={`review-goto-${item.key}`}
                        className="flex items-center gap-1 text-[11px] text-primary hover:underline shrink-0 mt-0.5">
                        Review <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                    {/* Persistent per-section sign-off */}
                    <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-border/60">
                      {item.acknowledged ? (
                        <span className="flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400"
                          data-testid={`review-ack-label-${item.key}`}>
                          <BadgeCheck className="w-3.5 h-3.5" />
                          Reviewed{item.acknowledged_by ? ` by ${item.acknowledged_by}` : ''}{item.acknowledged_at ? ` · ${fmtDate(item.acknowledged_at)}` : ''}
                        </span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">Not yet reviewed</span>
                      )}
                      {canReview && (
                        <button onClick={() => toggleAck(item.key)}
                          disabled={acking === item.key}
                          data-testid={`review-ack-toggle-${item.key}`}
                          className={`flex items-center gap-1 text-[11px] font-medium shrink-0 disabled:opacity-50 transition-colors ${
                            item.acknowledged
                              ? 'text-muted-foreground hover:text-foreground'
                              : 'text-emerald-600 dark:text-emerald-400 hover:underline'
                          }`}>
                          {acking === item.key ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : item.acknowledged ? (
                            <><RotateCcw className="w-3 h-3" /> Undo</>
                          ) : (
                            <><CheckCircle2 className="w-3 h-3" /> Mark reviewed</>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-muted/20">
          <button onClick={onClose} data-testid="handover-review-cancel"
            className="px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
            {mode === 'post-build' ? 'Close' : 'Cancel'}
          </button>
          {mode !== 'post-build' && onProceed && (
            <button
              onClick={() => { onProceed(); onClose(); }}
              disabled={loading}
              data-testid="handover-review-proceed"
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-wider rounded-sm transition-colors disabled:opacity-50 ${
                (clean || allAcked)
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  : 'bg-primary hover:bg-primary/90 text-primary-foreground'
              }`}>
              <Package className="w-3.5 h-3.5" />
              {(clean || allAcked)
                ? (mode === 'pre-email' ? 'Email handover' : 'Generate handover')
                : proceedLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
